import { useRef, useCallback, useEffect } from "react";
import useNotifications, {
  useReadNotification,
  useIntersection,
} from "../hooks/notification";

const Notifications = () => {
  const { notifications, unreadCount, loading, hasMore, loadMore } =
    useNotifications("notifications/");
  const { post } = useReadNotification();

  const observer = useRef(null);
  const unreadSeenRef = useRef(new Set()); // 🧠 store unread noti_ids seen in viewport

  const lastNotificationRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore, loadMore]
  );

  const handleNotificationSeen = (notiId) => {
    unreadSeenRef.current.add(notiId); // ✅ add to local set
  };

  useEffect(() => {
    return () => {
      const unreadIds = Array.from(unreadSeenRef.current);
      if (unreadIds.length > 0) {
        post(unreadIds);
      }
    };
  }, [post]);

  return (
    <div className="container mb-4">
      <h1 className="text-xl my-4 font-semibold text-medium-theme-color">
        Notifications {unreadCount > 0 && <>({unreadCount}) </>}
      </h1>

      {notifications.length === 0 && !loading && (
        <p className="text-gray-500 text-center mt-4">
          No Notifications Available
        </p>
      )}

      <div className="flex flex-col gap-4">
        {notifications.map((item, index) => (
          <NotificationItem
            key={item.noti_id}
            item={item}
            observerRef={
              index === notifications.length - 1 ? lastNotificationRef : null
            }
            onSeen={handleNotificationSeen}
          />
        ))}
      </div>

      {loading && (
        <div className="text-center my-4 text-gray-500">Loading...</div>
      )}
    </div>
  );
};

export default Notifications;

const NotificationItem = ({ item, onSeen, observerRef }) => {
  const ref = useRef(null);
  const hasReported = useRef(false);

  useIntersection(ref, () => {
    if (!item.is_read && !hasReported.current) {
      onSeen(item.noti_id);
      hasReported.current = true;
    }
  });

  const date = new Date(item.created_at).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div
      ref={observerRef || ref}
      className="relative bg-theme-color border p-3 px-4 rounded flex flex-col gap-3"
    >
      <p className="bg-medium-theme-color text-white self-start p-2 rounded-full text-xs">
        {item.noti_type}
      </p>
      <p className="text-lg text-medium-theme-color">{item.noti_text}</p>
      <small className="text-gray-500 self-end">{date}</small>
      {!item.is_read && (
        <span className="size-5 rounded-full bg-red-500 absolute -top-2.5 -right-2.5" />
      )}
    </div>
  );
};
